import { useRef, type KeyboardEvent } from 'react';
import { LayoutGrid, Rows, List } from '../../lib/icons.tsx';
import type { DeckViewMode } from './deckFilterState.ts';

/**
 * Grid / table / list, at the right-hand end of the filter bar.
 *
 * It is `.seg`'s shape but not `.seg`'s styling, and the difference is
 * deliberate. `.seg` answers a SETTING — a question with a sentence under it
 * saying what the current answer means — and marks its answer with the orange
 * fill so it reads as chosen. This switches which body renders the same decks,
 * where nothing has been decided and nothing changes but the view; an orange
 * fill would make the default look like an override every time the page loaded.
 * So the active segment is a raised `--panel` tile on the `--panel-2` track,
 * the way a view switcher reads everywhere else.
 *
 * It is a radiogroup because the three are one exclusive answer, with roving
 * tabindex (one tab stop for the group, arrows within it) per the ARIA pattern.
 * Each segment carries a real name — "Grid view", not an unlabelled square.
 */

export type ViewSwitcherProps = {
  value: DeckViewMode;
  onChange: (v: DeckViewMode) => void;
};

const VIEWS: { value: DeckViewMode; label: string; Icon: typeof LayoutGrid }[] = [
  { value: 'grid', label: 'Grid view', Icon: LayoutGrid },
  { value: 'rows', label: 'Rows view', Icon: Rows },
  { value: 'list', label: 'List view', Icon: List },
];

export default function ViewSwitcher({ value, onChange }: ViewSwitcherProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  // In a radiogroup an arrow key both moves AND selects, so focus has to follow
  // the value: the freshly checked radio is the only one still tabbable.
  function move(delta: number) {
    const from = VIEWS.findIndex((v) => v.value === value);
    const next = VIEWS[(((from + delta) % VIEWS.length) + VIEWS.length) % VIEWS.length];
    if (!next) return;
    onChange(next.value);
    groupRef.current?.querySelector<HTMLButtonElement>(`[data-view="${next.value}"]`)?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    }
  }

  return (
    <div className="view-switcher" role="radiogroup" aria-label="View" ref={groupRef} onKeyDown={onKeyDown}>
      {VIEWS.map(({ value: v, label, Icon }) => (
        <button
          key={v}
          type="button"
          role="radio"
          data-view={v}
          aria-checked={v === value}
          aria-label={label}
          tabIndex={v === value ? 0 : -1}
          className={'view-switcher__btn' + (v === value ? ' is-active' : '')}
          onClick={() => onChange(v)}
        >
          <Icon size={15} />
        </button>
      ))}
    </div>
  );
}
